// src/pages/Skills.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLang } from '../contexts/LanguageContext';

/* ── Types ─────────────────────────────────────── */
type Level = 'Expert' | 'Advanced' | 'Intermediate' | 'Basic';

interface Skill {
  name: string;
  level: Level;
}

interface SkillCategory {
  label: string;
  color: string;
  skills: Skill[];
}

/* ── Data ──────────────────────────────────────── */
const CATEGORIES: SkillCategory[] = [
  {
    label: 'Frontend',
    color: '#7c3aed',
    skills: [
      { name: 'React',             level: 'Expert'       },
      { name: 'TypeScript',        level: 'Expert'       },
      { name: 'Next.js',           level: 'Advanced'     },
      { name: 'styled-components', level: 'Expert'       },
      { name: 'Framer Motion',     level: 'Advanced'     },
      { name: 'HTML / CSS',        level: 'Expert'       },
      { name: 'Tailwind CSS',      level: 'Advanced'     },
    ],
  },
  {
    label: 'Backend',
    color: '#22c55e',
    skills: [
      { name: 'Node.js',        level: 'Expert'       },
      { name: 'Express',        level: 'Expert'       },
      { name: 'Go',             level: 'Intermediate' },
      { name: 'Rust',           level: 'Intermediate' },
      { name: 'PostgreSQL',     level: 'Advanced'     },
      { name: 'MongoDB',        level: 'Advanced'     },
      { name: 'REST / GraphQL', level: 'Advanced'     },
      { name: 'WebSockets',     level: 'Advanced'     },
    ],
  },
  {
    label: 'Blockchain',
    color: '#f59e0b',
    skills: [
      { name: 'Solidity',          level: 'Advanced'     },
      { name: 'Ethers.js / viem',  level: 'Advanced'     },
      { name: 'Hardhat',           level: 'Advanced'     },
      { name: 'Foundry',           level: 'Intermediate' },
      { name: 'IPFS',              level: 'Intermediate' },
      { name: 'Smart Contracts',   level: 'Advanced'     },
      { name: 'DeFi protocols',    level: 'Intermediate' },
    ],
  },
  {
    label: 'DevOps & Tools',
    color: '#06b6d4',
    skills: [
      { name: 'Git / GitHub', level: 'Expert'       },
      { name: 'Docker',       level: 'Advanced'     },
      { name: 'CI/CD',        level: 'Intermediate' },
      { name: 'Vercel',       level: 'Advanced'     },
      { name: 'Linux',        level: 'Advanced'     },
      { name: 'PowerBI',      level: 'Intermediate' },
      { name: 'Figma',        level: 'Intermediate' },
    ],
  },
];

const LEVEL_DOTS: Record<Level, number> = {
  Expert:       5,
  Advanced:     4,
  Intermediate: 3,
  Basic:        2,
};

const LEVEL_COLOR: Record<Level, string> = {
  Expert:       '#a78bfa',
  Advanced:     '#22c55e',
  Intermediate: '#f59e0b',
  Basic:        '#64748b',
};

/* ── Layout ────────────────────────────────────── */
const SkillsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 7rem 2rem 5rem;
  background: ${(p) => p.theme.gradients.background};
  min-height: 100vh;
`;

const SectionLabel = styled(motion.div)`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.accent};
  background: rgba(139,92,246,0.08);
  border: 1px solid rgba(139,92,246,0.22);
  padding: 0.35rem 1rem;
  border-radius: 50px;
  margin-bottom: 1.2rem;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 0.8rem;
  text-align: center;

  span {
    background: linear-gradient(135deg, #7c3aed, #a78bfa);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Subtitle = styled(motion.p)`
  color: ${(p) => p.theme.colors.muted};
  font-size: 1rem;
  max-width: 500px;
  text-align: center;
  margin-bottom: 3rem;
`;

/* ── Legend ────────────────────────────────────── */
const Legend = styled(motion.div)`
  display: flex;
  gap: 1.6rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  justify-content: center;
  padding: 0.9rem 1.5rem;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LegendDots = styled.span`
  display: flex;
  gap: 3px;
`;

const LegendDot = styled.span<{ $filled: boolean; $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.$filled ? p.$color : 'rgba(255,255,255,0.12)'};
`;

const LegendLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 500;
  color: ${(p) => p.theme.colors.muted};
  letter-spacing: 0.03em;
`;

/* ── Grid ──────────────────────────────────────── */
const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.4rem;
  width: 100%;
  max-width: 1100px;

  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;

const CategoryCard = styled(motion.div)<{ $color: string }>`
  background: #0e0e12;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 20px;
  padding: 1.8rem 2rem;
  position: relative;
  overflow: hidden;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: ${(p) => `linear-gradient(90deg, ${p.$color}, transparent)`};
    opacity: 0.75;
  }

  &:hover {
    border-color: rgba(255,255,255,0.11);
    box-shadow: 0 8px 32px rgba(0,0,0,0.3);
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.6rem;
`;

const CategoryDot = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  box-shadow: 0 0 8px ${(p) => p.$color}55;
  flex-shrink: 0;
`;

const CategoryTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.white};
  letter-spacing: -0.01em;
`;

/* ── Skill rows ────────────────────────────────── */
const SkillList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const SkillRow = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.58rem 0;
  border-bottom: 1px solid rgba(255,255,255,0.04);

  &:last-child { border-bottom: none; }
`;

const SkillName = styled.span`
  font-size: 0.87rem;
  font-weight: 500;
  color: ${(p) => p.theme.colors.light};
`;

const SkillRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex-shrink: 0;
`;

const LevelTag = styled.span<{ $color: string }>`
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: ${(p) => p.$color};
  min-width: 76px;
  text-align: right;
`;

const Dots = styled.div`
  display: flex;
  gap: 4px;
`;

const Dot = styled(motion.span)<{ $filled: boolean; $color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(p) => p.$filled ? p.$color : 'rgba(255,255,255,0.1)'};
  box-shadow: ${(p) => p.$filled ? `0 0 5px ${p.$color}50` : 'none'};
`;

/* ── Component ─────────────────────────────────── */
const Skills: React.FC = () => {
  const { t } = useLang();

  const legendItems: { level: Level; label: string }[] = [
    { level: 'Expert',       label: t.skills.levels.Expert       },
    { level: 'Advanced',     label: t.skills.levels.Advanced     },
    { level: 'Intermediate', label: t.skills.levels.Intermediate },
    { level: 'Basic',        label: t.skills.levels.Basic        },
  ];

  const cardVariants = {
    hidden:  { opacity: 0, y: 24 },
    visible: (i: number) => ({
      opacity: 1, y: 0,
      transition: { duration: 0.5, delay: i * 0.1 },
    }),
  };

  return (
    <SkillsContainer id="skills">
      <SectionLabel
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {t.skills.sectionLabel}
      </SectionLabel>

      <Title
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        {t.skills.title} <span>{t.skills.titleHighlight}</span>
      </Title>

      <Subtitle
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.6 }}
      >
        {t.skills.subtitle}
      </Subtitle>

      <Legend
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.28 }}
      >
        {legendItems.map(({ level, label }) => (
          <LegendItem key={level}>
            <LegendDots>
              {Array.from({ length: 5 }).map((_, i) => (
                <LegendDot key={i} $filled={i < LEVEL_DOTS[level]} $color={LEVEL_COLOR[level]} />
              ))}
            </LegendDots>
            <LegendLabel>{label}</LegendLabel>
          </LegendItem>
        ))}
      </Legend>

      <CategoriesGrid>
        {CATEGORIES.map((cat, ci) => (
          <CategoryCard
            key={cat.label}
            $color={cat.color}
            custom={ci}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
          >
            <CategoryHeader>
              <CategoryDot $color={cat.color} />
              <CategoryTitle>{cat.label}</CategoryTitle>
            </CategoryHeader>

            <SkillList>
              {cat.skills.map((skill, si) => {
                const filled = LEVEL_DOTS[skill.level];
                const color  = LEVEL_COLOR[skill.level];
                return (
                  <SkillRow
                    key={skill.name}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: ci * 0.06 + si * 0.04, duration: 0.3 }}
                  >
                    <SkillName>{skill.name}</SkillName>
                    <SkillRight>
                      <LevelTag $color={color}>
                        {t.skills.levels[skill.level]}
                      </LevelTag>
                      <Dots>
                        {Array.from({ length: 5 }).map((_, di) => (
                          <Dot
                            key={di}
                            $filled={di < filled}
                            $color={color}
                            initial={{ scale: 0.3, opacity: 0 }}
                            animate={{
                              scale: 1,
                              opacity: 1,
                              transition: {
                                delay: ci * 0.06 + si * 0.04 + di * 0.04,
                                duration: 0.22,
                              },
                            }}
                          />
                        ))}
                      </Dots>
                    </SkillRight>
                  </SkillRow>
                );
              })}
            </SkillList>
          </CategoryCard>
        ))}
      </CategoriesGrid>
    </SkillsContainer>
  );
};

export default Skills;
